import { Card } from 'components/Card';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  REMOTE_ASESETUP_FOLDERDELETE,
  REMOTE_ASESETUP_FOLDERSAVE,
} from 'store/actions';
import { selectAseSetupLibraryFolder } from 'store/remote/selectors';
import { AssessmentLibrary } from 'store/remote/types';
import { useAlert } from 'hooks/useAlert';
import { CardGeneric } from 'components/CardGeneric';
import { Button, ButtonTheme } from 'components/Button';

export const FolderScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYFOLDER>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const alert = useAlert();
  const forceUpdate = useForceUpdate();

  const { id: folderId } = route.params;

  const folder: AssessmentLibrary['folders'][string] = useSelector((state) =>
    selectAseSetupLibraryFolder(state, folderId || -1),
  ) || {
    id: 0,
    name: '',
    ordering: 0,
  };

  const isDirty = React.useRef(false);
  const data = React.useRef(cloneDeep(folder));
  const checkIsDirty = () => !isEqual(folder, data.current);

  // Reset data if ID changes, then check isDirty
  if (data.current.id !== folder.id) {
    data.current = cloneDeep(folder);
  }
  isDirty.current = checkIsDirty();

  const shouldReload = () => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  };

  const handleChangeWithName = <T extends keyof typeof data.current>(
    name: T,
    value: (typeof data.current)[T],
  ) => {
    data.current = {
      ...data.current,
      [name]: value,
    };
    shouldReload();
  };

  const handleSavePress = () => {
    if (!data.current.name.trim()) {
      alert({
        text: 'Please enter a name',
      });
      return;
    }
    if (isDirty.current) {
      dispatch(
        REMOTE_ASESETUP_FOLDERSAVE({
          id: folderId,
          data: data.current,
        }),
      );
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSavePress,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleDelete = () => {
    dispatch(NAVIGATE_MODAL_BACK());
    dispatch(REMOTE_ASESETUP_FOLDERDELETE({ id: folder.id }));
  };

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={folderId ? 'Folder' : 'New Folder'}
          onPressCancelButton={handleClose}
          cancelButtonText="Close"
          onPressSaveButton={
            folderId || isDirty.current ? handleSavePress : null
          }
        />
      }
    >
      <Card>
        <TextAreaField
          name="name"
          onChangeWithName={handleChangeWithName}
          placeholder="Name"
          value={data.current.name}
          onEsc={handleClose}
          autoFocus
          autoFocusDelay
        />
      </Card>
      {!!folder.id && (
        <CardGeneric>
          <Button
            text="Delete Folder"
            destructive
            onPress={handleDelete}
            buttonTheme={ButtonTheme.themeless}
            style={{ alignSelf: 'center' }}
          />
        </CardGeneric>
      )}
    </PageScrollView>
  );
};
